"use client";

import { api } from "@agroproj/backend/convex/_generated/api";
import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { useDebounce } from "use-debounce";
import { useQueryScope, useStablePaginatedQuery, useStableQuery } from "@/hooks/use-stable-query";
import type { ProducerView } from "@/lib/producers";
import { normalizeProducers } from "@/lib/producers";
import { buildProducersViewModel } from "../lib/producer-table-helpers";
type UseProducersDataParams = {
  /** Name, company name, or CPF/CNPJ; the backend tells them apart. */
  search?: string;
  documentType?: "cpf" | "cnpj" | "";
  /** "Cidade / UF" keys from `getProducerLocations`. */
  location?: string[];
  status?: string[];
  page?: string;
  pageSize?: number;
};
export function useProducersData({
  search = "",
  documentType = "",
  location = [],
  status = [],
  page = "1",
  pageSize = 10
}: UseProducersDataParams) {
  const [debouncedSearch] = useDebounce(search, 500);
  const organization = useQueryScope();
  const previousPage = useRef<{
    organization: typeof organization;
    rows: ProducerView[];
  } | undefined>(undefined);
  const statusFilter = useMemo((): "active" | "inactive" | undefined => {
    const value = status[0];
    if (status.length === 1 && (value === "active" || value === "inactive")) {
      return value;
    }
    return undefined;
  }, [status]);
  const paginatedArgs = useMemo(() => ({
    search: debouncedSearch.trim().length >= 2 ? debouncedSearch.trim().toLowerCase() : undefined,
    status: statusFilter,
    documentType: documentType || undefined,
    locations: location.length > 0 ? [...new Set(location)].sort() : undefined
  }), [debouncedSearch, documentType, location, statusFilter]);

  // Sparse searches scan more candidates per request while the UI keeps 25-row pages.
  const queryPageSize = paginatedArgs.search || paginatedArgs.locations ? 100 : pageSize;
  const {
    results: rawProducers,
    loadMore,
    status: paginationStatus,
    isStale
  } = useStablePaginatedQuery(api.producers.queries.getProducersPaginated, paginatedArgs, {
    initialNumItems: queryPageSize
  });

  // Options cover every producer, not just the loaded pages.
  const {
    data: locationOptions
  } = useStableQuery(api.producers.queries.getProducerLocations, {}, {});
  const normalizedProducers = useMemo<ProducerView[]>(() => normalizeProducers(rawProducers), [rawProducers]);
  const viewModel = useMemo(() => buildProducersViewModel({
    producers: normalizedProducers,
    page,
    pageSize,
    status: paginationStatus
  }), [normalizedProducers, page, pageSize, paginationStatus]);

  // Keep the displayed numbered page while the requested page loads.
  if (previousPage.current?.organization !== organization || !organization || paginationStatus === "LoadingFirstPage" && !isStale) {
    previousPage.current = undefined;
  }
  const isPagePending = isStale || viewModel.shouldLoadMore || paginationStatus === "LoadingMore" && viewModel.pageProducers.length < pageSize;
  const keptPage = isPagePending ? previousPage.current?.rows : undefined;
  if (!isPagePending && paginationStatus !== "LoadingFirstPage") {
    previousPage.current = {
      organization,
      rows: viewModel.pageProducers
    };
  }
  const isDisplayedPageStale = isStale || keptPage !== undefined;
  useLayoutEffect(() => {
    if (viewModel.shouldLoadMore) {
      loadMore(queryPageSize);
    }
  }, [loadMore, queryPageSize, viewModel.shouldLoadMore]);
  const prefetchNextPage = useCallback(() => {
    if (viewModel.shouldPrefetch) {
      loadMore(queryPageSize);
    }
    return null;
  }, [loadMore, queryPageSize, viewModel.shouldPrefetch]);
  return {
    producers: keptPage ?? viewModel.pageProducers,
    paginationInfo: viewModel.paginationInfo,
    uniqueLocations: locationOptions ?? [],
    isLoadingProducers: !isDisplayedPageStale && (viewModel.isLoadingProducers || isPagePending && viewModel.pageProducers.length === 0),
    isStale: isDisplayedPageStale,
    prefetchNextPage: viewModel.paginationInfo.hasMore ? prefetchNextPage : null,
    isSearching: search !== debouncedSearch
  };
}